// src/data/publishablePapers.js
//
// The papers cleared for publication as public, indexable HTML.
//
// ─── WHY THIS IS AN ALLOWLIST AND NOT "PUBLISH EVERYTHING" ───────────────────
// public/data/exams/ holds 99 papers, and not all are ours to republish. The three
// ÖSD base `*-mock-001.json` files are ÖSD's own Modelltests, unchanged since the
// original import. (The telc and Goethe base files were once transcriptions too, but
// were rewritten with our own texts in c52db9a; the telc ones kept a stale
// metadata.source naming the publisher PDF for months afterwards, which is why that
// field must never be read as provenance.) Republishing a publisher's reading
// passages would invite a takedown that costs far more than the ranking is worth.
//
// `metadata.source` cannot be used to tell them apart: every variant inherited the
// base file's metadata block verbatim, so all 11 telc B1 papers claim to come from
// the telc PDF regardless of whether their text is original.
//
// So membership here is earned by measurement, not by filename. Papers are picked
// from `node scripts/audit-paper-originality.mjs`, which compares each variant's
// 8-word sequences against the base paper and separates two kinds of overlap:
//   • rubric wording ("Sie hören jeden Text zweimal") — unavoidable in a
//     format-faithful mock, and not copyrightable expression;
//   • reading-passage prose — the publisher's material, disqualifying.
// Only papers whose PASSAGE overlap is ≤2% appear below.
//
// ─── ALL NINE EXAMS ARE NOW REPRESENTED ─────────────────────────────────────
// telc B2 and ÖSD B1 were initially blocked outright: 0 of 11 and 0 of 6 papers were
// clean. The cause was much narrower than it first appeared — every reading, grammar
// and listening section was already original, and the contamination sat in exactly
// ONE speaking stimulus per paper, byte-identical across all 17 variants because each
// inherited it from the base file (telc B2 "Getrennte Schulen für Mädchen und
// Jungen?"; ÖSD B1 the "Mit oder ohne Auto in die Stadt?" statements).
// scripts/replace-contaminated-speaking-prompts.mjs swapped both for original
// stimuli of the same shape, level and register, taking all 17 to 0.000 overlap.
//
// ─── WHICH PAPER IS FREE ────────────────────────────────────────────────────
// This list IS the definition of "the free paper", in all three places it appears:
// the public SEO page at /en/mock-exams/{slug}/practice-paper-1, the anonymous /try
// guest exam, and the free tier inside the app. examService.getAvailableExams()
// reorders each exam's picker so this paper is FIRST, so "the first one" and "the
// free one" cannot drift apart — they are the same row by construction.
//
// Chosen per exam as the best paper that is simultaneously:
//   • originality-clean   (scripts/audit-paper-originality.mjs)
//   • complete            (5 sections for telc/ÖSD, 4 for the modular Goethe papers)
//   • not hidden          (no excluding metadata.status)
//   • richest in EXPLANATIONS, so the free paper teaches rather than just answering
//
// telc and Goethe land on Logami papers, which carry 30–48 bilingual explanations.
// ÖSD lands on Claude 1 because NO ÖSD paper in the corpus has explanations at all —
// all 18 variants have answer keys only. Generating them is the open item there.
//
// The paper is fully open, answers and explanations included.

/** @typedef {{ examSlug: string, paperSlug: string, file: string, label: string }} PublishablePaper */

/** @type {PublishablePaper[]} */
export const PUBLISHABLE_PAPERS = [
  {
    examSlug: 'telc-b1',
    paperSlug: 'practice-paper-1',
    file: 'telc/b1/telc-deutsch-b1-mock-001 - Logami 1.json',
    label: 'Practice paper 1',
  },
  {
    examSlug: 'telc-b2',
    paperSlug: 'practice-paper-1',
    file: 'telc/b2/telc-deutsch-b2-mock-001 - Logami 1.json',
    label: 'Practice paper 1',
  },
  {
    examSlug: 'telc-c1',
    paperSlug: 'practice-paper-1',
    file: 'telc/c1/telc-deutsch-c1-mock-001 - Logami 1.json',
    label: 'Practice paper 1',
  },
  {
    examSlug: 'goethe-b1',
    paperSlug: 'practice-paper-1',
    file: 'goethe/b1/goethe-deutsch-b1-mock-001 - Logami 3.json',
    label: 'Practice paper 1',
  },
  {
    examSlug: 'goethe-b2',
    paperSlug: 'practice-paper-1',
    file: 'goethe/b2/goethe-deutsch-b2-mock-001 - Logami 6.json',
    label: 'Practice paper 1',
  },
  {
    examSlug: 'goethe-c1',
    paperSlug: 'practice-paper-1',
    file: 'goethe/c1/goethe-deutsch-c1-mock-001 - Logami 1.json',
    label: 'Practice paper 1',
  },
  {
    examSlug: 'osd-b1',
    paperSlug: 'practice-paper-1',
    file: 'osd/b1/osd-deutsch-b1-mock-001 - Claude 1.json',
    label: 'Practice paper 1',
  },
  {
    examSlug: 'osd-b2',
    paperSlug: 'practice-paper-1',
    file: 'osd/b2/osd-deutsch-b2-mock-001 - Claude 1.json',
    label: 'Practice paper 1',
  },
  {
    examSlug: 'osd-c1',
    paperSlug: 'practice-paper-1',
    file: 'osd/c1/osd-deutsch-c1-mock-001 - Claude 1.json',
    label: 'Practice paper 1',
  },
];

/** Papers published for a given exam slug. */
export function papersForExam(examSlug) {
  return PUBLISHABLE_PAPERS.filter((p) => p.examSlug === examSlug);
}

export function getPublishablePaper(examSlug, paperSlug) {
  return PUBLISHABLE_PAPERS.find((p) => p.examSlug === examSlug && p.paperSlug === paperSlug) || null;
}

/** Fetchable URL for a paper's JSON (filenames contain spaces). */
export function paperDataUrl(paper) {
  return `/data/exams/${paper.file.split('/').map(encodeURIComponent).join('/')}`;
}
